import { Box, Typography, Button, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LayersIcon from "@mui/icons-material/Layers";
import SpeedIcon from "@mui/icons-material/Speed";
import BuildIcon from "@mui/icons-material/Build";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Header from "../../../global_components/parameterized/Header";
import drillData from "./drillOverview.json";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";

// ─── Constants ────────────────────────────────────────────────────────────────

// GitLab repository for this dataset (same as DrillExperiments.jsx)
const GITLAB_URL =
  "https://git.rwth-aachen.de/nfdi4ing/education/exampledatasets/drillprocess-timeseries-machinelearning/";

// ─── Date parsing ─────────────────────────────────────────────────────────────

const parseDMY = (str) => {
  const [d, m, y] = str.split(".");
  return new Date(`${y}-${m}-${d}`);
};

const fmtDate = (str) =>
  parseDMY(str).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const fmtDateWithoutYear = (str) =>
  parseDMY(str).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });

// ─── Metadata helpers ─────────────────────────────────────────────────────────

const getMachineLabel = (r) => {
  const match = r.filename.match(/drill_(\d+)/);
  return match ? `Machine ${match[1]}` : "Machine";
};

const getShortMachineLabel = (r) => {
  const match = r.filename.match(/drill_(\d+)/);
  return match ? `M${match[1]}` : "M";
};

const getFailureLabel = (failureTypes = []) => {
  const hasTool = failureTypes.includes("Tool");
  const hasTemp = failureTypes.includes("Temp");

  if (hasTool && hasTemp) return "Both";
  if (hasTool) return "Tool wear";
  if (hasTemp) return "Overheating";
  return "No Failure";
};

const getFailureStyle = (failure) => {
  if (failure === "Tool wear") return { bg: "#EAF1F8", color: "#40576D" };
  if (failure === "Overheating") return { bg: "#F3EEE8", color: "#6C5A45" };
  if (failure === "Both") return { bg: "#EFEAE2", color: "#6B5B48" };
  return { bg: "#F2F4F7", color: "#667085" };
};

// ─── KPI values ───────────────────────────────────────────────────────────────

const totalRuns = drillData.length;

const avgTorque = (
  drillData.reduce((s, r) => s + r.avg_torque_Nm, 0) / totalRuns
).toFixed(1);

const avgSpeed = (
  drillData.reduce((s, r) => s + r.avg_speed_rpm, 0) / totalRuns
).toFixed(1);

const avgProcTemp = (
  drillData.reduce((s, r) => s + r.avg_process_temp_C, 0) / totalRuns
).toFixed(1);

const sortedDates = [...drillData].sort(
  (a, b) => parseDMY(a.date) - parseDMY(b.date),
);

const dateRange = `${fmtDate(sortedDates[0].date)} – ${fmtDate(
  sortedDates[sortedDates.length - 1].date,
)}`;

const shiftsCount = new Set(drillData.map((r) => r.shift)).size;
const machinesCount = new Set(drillData.map((r) => getShortMachineLabel(r)))
  .size;

const hasToolWear = drillData.some((r) =>
  (r.failure_types ?? []).includes("Tool"),
);

const hasOverheat = drillData.some((r) =>
  (r.failure_types ?? []).includes("Temp"),
);

const hasBoth = drillData.some(
  (r) =>
    (r.failure_types ?? []).includes("Tool") &&
    (r.failure_types ?? []).includes("Temp"),
);

// ─── Chart data ───────────────────────────────────────────────────────────────

const failureCounts = (() => {
  const counts = { Tool: 0, Temp: 0, Both: 0, None: 0 };

  drillData.forEach((r) => {
    const ft = r.failure_types ?? [];
    const hasTool = ft.includes("Tool");
    const hasTemp = ft.includes("Temp");

    if (hasTool && hasTemp) counts.Both++;
    else if (hasTool) counts.Tool++;
    else if (hasTemp) counts.Temp++;
    else counts.None++;
  });

  return [
    { type: "Tool wear", count: counts.Tool, fill: "#378ADD" },
    { type: "Overheating", count: counts.Temp, fill: "#D85A30" },
    { type: "Both", count: counts.Both, fill: "#BA7517" },
    { type: "No failure", count: counts.None, fill: "#00C49F" },
  ];
})();

const shiftSpeedTorque = ["Shift1", "Shift2", "Shift3"].map((sh) => {
  const rows = drillData.filter((r) => r.shift === sh);

  return {
    shift: sh.replace("Shift", "Shift "),
    avg_speed: parseFloat(
      (rows.reduce((s, r) => s + r.avg_speed_rpm, 0) / rows.length).toFixed(1),
    ),
    avg_torque: parseFloat(
      (rows.reduce((s, r) => s + r.avg_torque_Nm, 0) / rows.length).toFixed(1),
    ),
  };
});

const shiftTemps = ["Shift1", "Shift2", "Shift3"].map((sh) => {
  const rows = drillData.filter((r) => r.shift === sh);

  return {
    shift: sh.replace("Shift", "Shift "),
    avg_air: parseFloat(
      (rows.reduce((s, r) => s + r.avg_air_temp_C, 0) / rows.length).toFixed(1),
    ),
    avg_proc: parseFloat(
      (
        rows.reduce((s, r) => s + r.avg_process_temp_C, 0) / rows.length
      ).toFixed(1),
    ),
  };
});

// ─── Third-row data ───────────────────────────────────────────────────────────

const matrixDates = [...new Set(drillData.map((r) => r.date))].sort(
  (a, b) => parseDMY(a) - parseDMY(b),
);

const matrixShifts = ["Shift1", "Shift2", "Shift3"];

const failureOverviewMatrix = matrixDates.map((date) => ({
  date,
  label: fmtDateWithoutYear(date),
  cells: matrixShifts.map((shift) => {
    const rows = drillData
      .filter((r) => r.date === date && r.shift === shift)
      .sort((a, b) => getMachineLabel(a).localeCompare(getMachineLabel(b)))
      .map((r) => ({
        machine: getShortMachineLabel(r),
        failure: getFailureLabel(r.failure_types),
      }));

    return { shift, rows };
  }),
}));

const lowestSpeedRun = [...drillData].sort(
  (a, b) => a.avg_speed_rpm - b.avg_speed_rpm,
)[0];

const highestAvgSpeedRun = [...drillData].sort(
  (a, b) => b.avg_speed_rpm - a.avg_speed_rpm,
)[0];

const highestTorqueRun = [...drillData].sort(
  (a, b) => b.max_torque_Nm - a.max_torque_Nm,
)[0];

const highestAvgTorqueRun = [...drillData].sort(
  (a, b) => b.avg_torque_Nm - a.avg_torque_Nm,
)[0];

const highestTempRun = [...drillData].sort(
  (a, b) => b.avg_process_temp_C - a.avg_process_temp_C,
)[0];

const operatingInsights = [
  {
    insight: "Minimum Mean Speed",
    date: fmtDate(lowestSpeedRun.date),
    shift: lowestSpeedRun.shift.replace("Shift", "Shift "),
    machine: getShortMachineLabel(lowestSpeedRun),
    value: `${lowestSpeedRun.avg_speed_rpm.toFixed(1)} rpm`,
  },
  {
    insight: "Maximum Mean Speed",
    date: fmtDate(highestAvgSpeedRun.date),
    shift: highestAvgSpeedRun.shift.replace("Shift", "Shift "),
    machine: getShortMachineLabel(highestAvgSpeedRun),
    value: `${highestAvgSpeedRun.avg_speed_rpm.toFixed(1)} rpm`,
  },
  {
    insight: "Maximum Peak Torque",
    date: fmtDate(highestTorqueRun.date),
    shift: highestTorqueRun.shift.replace("Shift", "Shift "),
    machine: getShortMachineLabel(highestTorqueRun),
    value: `${highestTorqueRun.max_torque_Nm.toFixed(1)} Nm`,
  },
  {
    insight: "Maximum Mean Torque",
    date: fmtDate(highestAvgTorqueRun.date),
    shift: highestAvgTorqueRun.shift.replace("Shift", "Shift "),
    machine: getShortMachineLabel(highestAvgTorqueRun),
    value: `${highestAvgTorqueRun.avg_torque_Nm.toFixed(1)} Nm`,
  },
  {
    insight: "Maximum Process Temperature",
    date: fmtDate(highestTempRun.date),
    shift: highestTempRun.shift.replace("Shift", "Shift "),
    machine: getShortMachineLabel(highestTempRun),
    value: `${highestTempRun.avg_process_temp_C.toFixed(1)} °C`,
  },
];

// ─── Custom label components ──────────────────────────────────────────────────

const TwoLineLabel = ({ x, y, width, value, unit }) => (
  <text textAnchor="middle" fontSize={11} fill="#555">
    <tspan x={x + width / 2} y={y - 18}>
      {value}
    </tspan>
    <tspan x={x + width / 2} y={y - 5}>
      {unit}
    </tspan>
  </text>
);

const SpeedLabel = (props) => <TwoLineLabel {...props} unit="rpm" />;
const TorqueLabel = (props) => <TwoLineLabel {...props} unit="Nm" />;

// ─── Tooltip ──────────────────────────────────────────────────────────────────

const BasicTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #ccc",
        p: "10px",
        borderRadius: 1,
      }}
    >
      {label && (
        <Typography variant="caption" display="block" fontWeight={600}>
          {label}
        </Typography>
      )}

      {payload.map((p, i) => (
        <Typography
          key={i}
          variant="caption"
          display="block"
          sx={{ color: p.color }}
        >
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(1) : p.value}
        </Typography>
      ))}
    </Box>
  );
};

// ─── Shared components ────────────────────────────────────────────────────────

const FailureChip = ({ label, bg, color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: "13px",
      fontWeight: 600,
      px: "10px",
      py: "4px",
      borderRadius: "99px",
      backgroundColor: bg,
      color,
    }}
  >
    {label}
  </Box>
);

const DrillStatBox = ({ title, subtitle, icon, colors }) => (
  <Box
    width="100%"
    m="0 30px"
    display="flex"
    justifyContent="space-between"
    alignItems="center"
  >
    <Box>
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ color: colors.grey[100], mb: "4px" }}
      >
        {title}
      </Typography>

      <Typography variant="h5" mt="2px" sx={{ color: colors.greenAccent[500] }}>
        {subtitle}
      </Typography>
    </Box>

    <Box display="flex" alignItems="center">
      {icon}
    </Box>
  </Box>
);

const ChartHeader = ({ title, colors }) => (
  <Box
    mt="25px"
    p="0 30px"
    display="flex"
    justifyContent="space-between"
    alignItems="center"
  >
    <Typography variant="h5" fontWeight="600" color={colors.grey[100]}>
      {title}
    </Typography>
  </Box>
);

const FailureTypeBadge = ({ failure }) => {
  const style = getFailureStyle(failure);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: "8px",
        py: "3px",
        borderRadius: "99px",
        backgroundColor: style.bg,
        color: style.color,
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {failure}
    </Box>
  );
};

// ─── Third row card layout ────────────────────────────────────────────────────

const InsightCard = ({ title, colors, children }) => (
  <Box
    backgroundColor={colors.primary[400]}
    minHeight="420px"
    height="100%"
    display="flex"
    flexDirection="column"
    sx={{
      boxSizing: "border-box",
      overflow: "hidden",
    }}
  >
    <ChartHeader title={title} colors={colors} />

    <Box
      flex={1}
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        px: "30px",
        pt: "10px",
        pb: "22px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box width="100%" maxWidth="100%">
        {children}
      </Box>
    </Box>
  </Box>
);

// ─── Failure overview matrix ──────────────────────────────────────────────────

const FailureMatrixCell = ({ rows }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: "6px",
      minHeight: "62px",
      px: "9px",
      py: "7px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: "6px",
      boxSizing: "border-box",
    }}
  >
    {rows.length > 0 ? (
      rows.map((item, idx) => (
        <Box
          key={idx}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap="6px"
          sx={{
            borderBottom:
              idx !== rows.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
            pb: idx !== rows.length - 1 ? "5px" : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#2D3748",
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
          >
            {item.machine}
          </Typography>

          <FailureTypeBadge failure={item.failure} />
        </Box>
      ))
    ) : (
      <Typography
        variant="caption"
        sx={{ textAlign: "center", color: "#6B7280", fontSize: "12px" }}
      >
        No data
      </Typography>
    )}
  </Box>
);

const FailureOverviewCard = ({ colors }) => (
  <InsightCard title="Failure overview by date and shift" colors={colors}>
    <Box
      display="grid"
      gridTemplateColumns="48px repeat(3, 1fr)"
      gap="6px"
      mb="8px"
      alignItems="center"
    >
      <Box />

      {matrixShifts.map((shift) => (
        <Typography
          key={shift}
          variant="caption"
          fontWeight={700}
          textAlign="center"
          sx={{ color: colors.grey[100], fontSize: "12px" }}
        >
          {shift.replace("Shift", "Shift ")}
        </Typography>
      ))}
    </Box>

    {failureOverviewMatrix.map((row) => (
      <Box
        key={row.date}
        display="grid"
        gridTemplateColumns="48px repeat(3, 1fr)"
        gap="6px"
        mb="7px"
        alignItems="center"
      >
        <Typography
          variant="caption"
          fontWeight={700}
          textAlign="center"
          sx={{
            color: colors.grey[100],
            fontSize: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {row.label}
        </Typography>

        {row.cells.map((cell, idx) => (
          <FailureMatrixCell key={`${row.date}-${idx}`} rows={cell.rows} />
        ))}
      </Box>
    ))}
  </InsightCard>
);

// ─── Clean centered table ─────────────────────────────────────────────────────

const CleanTable = ({ columns, rows }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: "100%",
      backgroundColor: "#fff",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: "8px",
      overflow: "hidden",
      boxSizing: "border-box",
    }}
  >
    <Box
      display="grid"
      gridTemplateColumns={columns.map((c) => c.width).join(" ")}
      sx={{
        backgroundColor: "#F8FAFC",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      {columns.map((col) => (
        <Typography
          key={col.key}
          variant="caption"
          textAlign="center"
          sx={{
            color: "#2D3748",
            fontSize: "13px",
            fontWeight: 700,
            py: "12px",
            px: "8px",
            whiteSpace: "nowrap",
          }}
        >
          {col.label}
        </Typography>
      ))}
    </Box>

    {rows.map((row, rowIndex) => {
      const isLast = rowIndex === rows.length - 1;

      return (
        <Box
          key={rowIndex}
          display="grid"
          gridTemplateColumns={columns.map((c) => c.width).join(" ")}
          sx={{
            borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.06)",
            minHeight: "60px",
            alignItems: "center",
          }}
        >
          {columns.map((col) => (
            <Typography
              key={col.key}
              variant="caption"
              textAlign="center"
              sx={{
                color: "#2D3748",
                fontSize: "13px",
                fontWeight: col.bold ? 700 : 600,
                py: "11px",
                px: "8px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={row[col.key]}
            >
              {row[col.key]}
            </Typography>
          ))}
        </Box>
      );
    })}
  </Box>
);

// ─── Operating insights ───────────────────────────────────────────────────────

const OperatingInsightsCard = ({ colors }) => {
  const columns = [
    { key: "insight", label: "Insight", width: "1.25fr" },
    { key: "date", label: "Date", width: "0.8fr" },
    { key: "machine", label: "Machine", width: "0.8fr" },
    { key: "shift", label: "Shift", width: "0.95fr" },
    { key: "value", label: "Value", width: "0.95fr", bold: true },
  ];

  return (
    <InsightCard title="Key Observations" colors={colors}>
      <CleanTable columns={columns} rows={operatingInsights} />
    </InsightCard>
  );
};

// ─── Third row ────────────────────────────────────────────────────────────────

const ThirdRowInsights = ({ colors }) => (
  <Box
    display="grid"
    gridTemplateColumns="repeat(12, 1fr)"
    gap="20px"
    mt="20px"
    alignItems="stretch"
  >
    <Box gridColumn="span 6" minWidth={0}>
      <FailureOverviewCard colors={colors} />
    </Box>

    <Box gridColumn="span 6" minWidth={0}>
      <OperatingInsightsCard colors={colors} />
    </Box>
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const DrillOverview = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box m="20px">
      <Box position="relative" mb="8px">
        <Header
          title="DRILL EXPERIMENTS"
          subtitle="Overview of all experiment runs"
        />

        {/* GitLab dataset link — anchored to top-right, hangs slightly below
            the header block so it visually overlaps the top of the KPI cards */}
        <Button
          component="a"
          href={GITLAB_URL}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
          variant="contained"
          disableElevation
          sx={{
            position: "absolute",
            right: 0,
            bottom: "-14px",
            zIndex: 2,
            backgroundColor: "#11192A",
            color: "#fff",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.85rem",
            borderRadius: "4px",
            px: 1.75,
            py: 0.75,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            "&:hover": { backgroundColor: "#1f2a45" },
          }}
        >
          View dataset on GitLab
        </Button>
      </Box>

      {/* KPI ROW */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px"
        mb="20px"
      >
        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <DrillStatBox
            title={dateRange}
            subtitle="Date range"
            icon={
              <CalendarTodayIcon
                sx={{ color: colors.greenAccent[600], fontSize: "32px" }}
              />
            }
            colors={colors}
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <DrillStatBox
            title={`${shiftsCount} shifts · ${machinesCount} machines`}
            subtitle={`${totalRuns} Experiment Runs`}
            icon={
              <LayersIcon
                sx={{ color: colors.greenAccent[600], fontSize: "32px" }}
              />
            }
            colors={colors}
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <DrillStatBox
            title={`${avgSpeed} rpm`}
            subtitle="Mean Rotational Speed"
            icon={
              <SpeedIcon
                sx={{ color: colors.greenAccent[600], fontSize: "32px" }}
              />
            }
            colors={colors}
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <DrillStatBox
            title={`${avgTorque} Nm`}
            subtitle="Mean Torque Load"
            icon={
              <BuildIcon
                sx={{ color: colors.greenAccent[600], fontSize: "32px" }}
              />
            }
            colors={colors}
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <DrillStatBox
            title={`${avgProcTemp} °C`}
            subtitle="Mean Process Temperature"
            icon={
              <ThermostatIcon
                sx={{ color: colors.greenAccent[600], fontSize: "32px" }}
              />
            }
            colors={colors}
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box width="100%" m="0 30px">
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ color: colors.grey[100], mb: "8px" }}
            >
              Observed Failure Types
            </Typography>

            <Box display="flex" flexWrap="wrap" gap="6px">
              {hasToolWear && (
                <FailureChip label="Tool wear" bg="#EAF1F8" color="#40576D" />
              )}

              {hasOverheat && (
                <FailureChip label="Overheating" bg="#F3EEE8" color="#6C5A45" />
              )}

              {hasBoth && (
                <FailureChip label="Both" bg="#EFEAE2" color="#6B5B48" />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* CHARTS GRID */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="380px"
        gap="20px"
        mb="0"
      >
        <Box gridColumn="span 4" backgroundColor={colors.primary[400]}>
          <ChartHeader
            title="Distribution of Failure Occurrences across Experiment Runs"
            colors={colors}
          />

          <Box height="300px" mt="10px">
            <ResponsiveContainer>
              <BarChart
                data={failureCounts}
                margin={{ top: 20, right: 60, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis
                  domain={[0, totalRuns + 2]}
                  allowDataOverflow={false}
                  width={45}
                />
                <Tooltip content={<BasicTooltip />} />
                <Bar
                  dataKey="count"
                  name="Runs"
                  barSize={52}
                  isAnimationActive={false}
                  label={{ position: "top", fontSize: 12, fill: "#555" }}
                >
                  {failureCounts.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box gridColumn="span 4" backgroundColor={colors.primary[400]}>
          <ChartHeader
            title="Mean Speed and Torque per Shift"
            colors={colors}
          />

          <Box height="300px" mt="10px">
            <ResponsiveContainer>
              <BarChart
                data={shiftSpeedTorque}
                margin={{ top: 40, right: 60, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="shift" />
                <YAxis
                  yAxisId="speed"
                  orientation="left"
                  width={45}
                  domain={[0, 140]}
                  label={{
                    value: "rpm",
                    angle: -90,
                    position: "insideLeft",
                    offset: 10,
                    style: { fontSize: 10 },
                  }}
                />
                <YAxis
                  yAxisId="torque"
                  orientation="right"
                  width={45}
                  domain={[0, 50]}
                  label={{
                    value: "Nm",
                    angle: 90,
                    position: "insideRight",
                    offset: 10,
                    style: { fontSize: 10 },
                  }}
                />
                <Tooltip content={<BasicTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  layout="horizontal"
                  wrapperStyle={{ paddingTop: "8px" }}
                />
                <Bar
                  yAxisId="speed"
                  dataKey="avg_speed"
                  name="Mean Speed (rpm)"
                  fill="#4590F9"
                  barSize={26}
                  label={{ content: SpeedLabel }}
                />
                <Bar
                  yAxisId="torque"
                  dataKey="avg_torque"
                  name="Mean Torque (Nm)"
                  fill="#FF6B6B"
                  barSize={26}
                  label={{ content: TorqueLabel }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box gridColumn="span 4" backgroundColor={colors.primary[400]}>
          <ChartHeader
            title="Air and Process Temperature by Shift"
            colors={colors}
          />

          <Box height="300px" mt="10px">
            <ResponsiveContainer>
              <BarChart
                data={shiftTemps}
                margin={{ top: 20, right: 60, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="shift" />
                <YAxis
                  unit="°C"
                  width={45}
                  domain={[0, 90]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90]}
                />
                <Tooltip content={<BasicTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  layout="horizontal"
                  wrapperStyle={{ paddingTop: "8px" }}
                />
                <Bar
                  dataKey="avg_air"
                  name="Air temp (°C)"
                  fill="#378ADD"
                  barSize={26}
                  label={{
                    content: ({ x, y, width, value }) => (
                      <text
                        x={x + width / 2}
                        y={y - 6}
                        textAnchor="middle"
                        fontSize={11}
                        fill="#555"
                      >{`${value}°C`}</text>
                    ),
                  }}
                />
                <Bar
                  dataKey="avg_proc"
                  name="Process temp (°C)"
                  fill="#1D9E75"
                  barSize={26}
                  label={{
                    content: ({ x, y, width, value }) => (
                      <text
                        x={x + width / 2}
                        y={y - 6}
                        textAnchor="middle"
                        fontSize={11}
                        fill="#555"
                      >{`${value}°C`}</text>
                    ),
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>

      <ThirdRowInsights colors={colors} />
    </Box>
  );
};

export default DrillOverview;
